<template>
  <div class="notification-bell">
    <b-dropdown
      size="sm"
      variant="link"
      toggle-class="text-decoration-none"
      no-caret
      right
    >
      <template #button-content>
        <div class="position-relative">
          <i class="fas fa-bell fa-lg text-white"></i>
          <span
            v-if="unreadCount > 0"
            class="badge badge-danger badge-pill position-absolute unread-badge"
            style="top: -10px; right: -10px; font-size: 0.7rem; padding: 0.25em; min-width: 1.6em; height: 1.6em; display: flex; align-items: center; justify-content: center; line-height: 1;"
          >
            {{ unreadCount }}
          </span>
        </div>
      </template>

      <b-dropdown-header class="text-dark font-weight-bold" style="font-size: 1.1rem; padding: 10px 15px;">
        Notificaciones
      </b-dropdown-header>
      
      <div v-if="loading" class="text-center p-2">
        <b-spinner small></b-spinner>
      </div>

      <div v-else-if="notifications.length === 0" class="p-3 text-muted text-center">
        <small>No tienes notificaciones</small>
      </div>

      <div class="notifications-scroll-area">
        <b-dropdown-item
          v-for="note in displayedNotifications"
          :key="note.id + note.type"
          class="notification-item"
          :class="{ 'unread-bg': !note.read }"
          @click="markAsRead(note)"
        >
          <div class="d-flex flex-column" style="width: 330px; max-width: 90vw; white-space: normal;">
            <div class="d-flex justify-content-between align-items-center">
              <strong :style="{ fontSize: '0.85rem', fontWeight: !note.read ? 'bold' : 'normal' }">{{ note.senderUsername }}</strong>
              <span v-if="!note.read" class="unread-dot"></span>
            </div>
            <small class="text-secondary">{{ formatDateTime(note.createdAt) }}</small>
            <span class="mt-1" style="font-size: 0.9rem;" :class="{ 'text-dark font-weight-bold': !note.read, 'text-muted': note.read }">{{ note.message }}</span>
          </div>
        </b-dropdown-item>
      </div>
      
    </b-dropdown>
  </div>
</template>

<script>
import { api1, api3 } from "@/lib/http";

export default {
  name: "NotificationBell",
  data() {
    return {
      notifications: [],
      loading: false,
      intervalId: null
    };
  },
  computed: {
    unreadCount() {
      return this.notifications.filter(n => !n.read).length;
    },
    displayedNotifications() {
      return this.notifications.slice(0, 10);
    }
  },
  mounted() {
    this.fetchNotifications();
    // Poll every 60 seconds
    this.intervalId = setInterval(this.fetchNotifications, 60000);
  },
  beforeUnmount() {
    if (this.intervalId) clearInterval(this.intervalId);
  },
  methods: {
    async fetchNotifications() {
      try {
        const username = JSON.parse(localStorage.getItem("user") || "{}")?.username;
        
        // Fetch recent from Formacion (api3)
        const resFormacion = await api3.get("notifications/recent").catch(() => ({ data: [] }));
        const notesFormacion = (resFormacion.data || []).map(n => ({ ...n, type: 'course' }));

        // Fetch recent from Projects (api1)
        let notesProjects = [];
        if (username) {
            const resProjects = await api1.get(`api/chat/proyecto/notifications/recent/${username}`).catch(() => ({ data: [] }));
            notesProjects = (resProjects.data || []).map(n => ({ ...n, type: 'project' }));

            // Fetch recent from Offers (api1)
            const resOffers = await api1.get(`api/chat/oferta/notifications/recent/${username}`).catch(() => ({ data: [] }));
            const notesOffers = (resOffers.data || []).map(n => ({ ...n, type: 'oferta' }));
            
            notesProjects = [...notesProjects, ...notesOffers];
        }

        // Merge, normalize and sort
        const allNotes = [...notesFormacion, ...notesProjects].map((n) => {
            // Normalization of 'read' flag
            const possibleReadKeys = ['read', 'isRead', 'is_read', 'leido', 'visto'];
            let isReadVal = undefined;
            
            for (const key of possibleReadKeys) {
                if (n[key] !== undefined) {
                    isReadVal = n[key];
                    break;
                }
            }
            
            if (isReadVal === undefined) isReadVal = false;
            
            let readBool = (typeof isReadVal === 'string') 
                ? (isReadVal.toLowerCase() === 'true' || isReadVal === '1') 
                : !!isReadVal;

            let rawDate = n.createdAt || n.created_at || n.fecha || n.date;

            // Normalize Date for sorting
            let dateObj = null;
            if (Array.isArray(rawDate)) {
                const [y, m, d, h, min] = rawDate;
                dateObj = new Date(y, m - 1, d, h, min);
            } else if (rawDate) {
                dateObj = new Date(rawDate);
            }

            return {
                ...n,
                read: readBool,
                createdAt: rawDate,
                _dateObj: dateObj // For robust sorting
            };
        });

        this.notifications = allNotes.sort((a, b) => {
            if (a.read !== b.read) {
                return a.read ? 1 : -1;
            }
            if (a._dateObj && b._dateObj) {
                return b._dateObj - a._dateObj;
            }
            return 0;
        });
      } catch (error) {
        console.error("Error fetching notifications", error);
      }
    },
    async markAsRead(notification) {
      try {
        if (!notification.read) {
            if (notification.type === 'project') {
                await api1.post(`api/chat/proyecto/notifications/${notification.id}/read`);
            } else if (notification.type === 'oferta') {
                await api1.post(`api/chat/oferta/notifications/${notification.id}/read`);
            } else {
                await api3.post(`notifications/${notification.id}/read`);
            }
            // Update locally instead of removing
            notification.read = true;
        }

        // Redirection
        if (notification.idCurso || (notification.type === 'course' && notification.idCurso)) {
            this.$router.push({ name: 'Curso', query: { openChat: (notification.idCurso || notification.id) } }).catch(()=>{});
        } else if (notification.idProject || (notification.type === 'project' && notification.idProject)) {
            this.$router.push({ name: 'detalle-proyecto', query: { openChatId: notification.idProject } }).catch(()=>{});
        } else if (notification.idOferta || (notification.type === 'oferta' && notification.idOferta)) {
            this.$router.push({ 
                name: 'ofertas', 
                query: { openChatId: notification.idOferta }
            }).catch(()=>{});
            // We emit an event because we might already be in the page
            this.$root.$emit('open-oferta-chat', notification.idOferta);
        }
      } catch (error) {
        console.error("Error marking notification as read", error);
      }
    },
    formatDateTime(dateStr) {
      if (!dateStr) return "";
      
      let dateObj = null;

      // Handle array format [y, m, d, h, min]
      if (Array.isArray(dateStr)) {
          const [y, m, d, h, min] = dateStr;
          dateObj = new Date(y, m - 1, d, h, min);
      } 
      // If it's a string, try to parse it
      else if (typeof dateStr === 'string') {
          dateObj = new Date(dateStr);
      } else {
          dateObj = new Date(dateStr);
      }

      if (!dateObj || isNaN(dateObj.getTime())) return "";

      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      const h = String(dateObj.getHours()).padStart(2, '0');
      const min = String(dateObj.getMinutes()).padStart(2, '0');

      return `${day}/${month}/${year} ${h}:${min}`;
    }
  }
};
</script>

<style scoped>
.notification-bell {
  margin-right: 15px;
  cursor: pointer;
}
.notification-item {
  border-bottom: 1px solid #eee;
  transition: background-color 0.2s;
}
.notification-item:last-child {
  border-bottom: none;
}
.notifications-scroll-area {
  max-height: 400px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: thin; /* Para Firefox */
}
/* Estilo del scroll para Chrome/Edge */
.notifications-scroll-area::-webkit-scrollbar {
  width: 6px;
}
.notifications-scroll-area::-webkit-scrollbar-track {
  background: #f1f1f1;
}
.notifications-scroll-area::-webkit-scrollbar-thumb {
  background: #ccc;
  border-radius: 10px;
}
.notifications-scroll-area::-webkit-scrollbar-thumb:hover {
  background: #bbb;
}
.unread-bg {
  background-color: #dbeafe !important;
  border-left: 6px solid #2563eb !important;
}
.unread-badge {
  font-weight: 800;
  border: none;
}
.unread-dot {
  width: 12px;
  height: 12px;
  background-color: #2563eb;
  border-radius: 50%;
  display: inline-block;
  box-shadow: 0 0 4px rgba(0,0,0,0.2);
}
@keyframes pulse {
  0% { transform: scale(1); }
  50% { transform: scale(1.1); }
  100% { transform: scale(1); }
}
.animate-pulse {
  animation: pulse 2s infinite;
}
.unread-dot {
  width: 8px;
  height: 8px;
  background-color: #007bff;
  border-radius: 50%;
  display: inline-block;
}
</style>
